/* De taal beschikbaar maken, en hem ook echt aan de pagina hangen.

   De taal wordt SYNCHROON afgeleid, tijdens de render. Eerst stond hij in
   state met een effect dat hem bijstelde zodra bekend werd wie er keek -- en
   dan zag R één frame Russisch voordat het naar Engels sprong. Een effect
   draait na de paint; dat frame is dus echt zichtbaar.

   Er is niets asynchroons aan deze beslissing: `localStorage` en de handle uit
   de bootstrapcache zijn er allebei al bij de eerste render. Alleen een
   expliciete tik van de gebruiker is state, en die hoort dat ook te zijn.

   `<html lang>` moet meebewegen: een schermlezer kiest zijn stem en zijn
   uitspraakregels daarop. Russische tekst voorgelezen met een Engelse stem is
   onverstaanbaar.

   Dat attribuut wordt tijdens de render gezet en niet in een effect. Effecten
   -- ook layout-effecten -- draaien ná het opbouwen van de boom, dus er is een
   moment waarop de eerste Engelse tekst al in de DOM staat terwijl `lang` nog
   op Russisch staat. Kort, maar een schermlezer die net dan begint te lezen
   pakt de verkeerde stem. Het is één idempotente attribuutschrijving; een
   herhaalde render doet niets. */
import { useCallback, useMemo, useState, type ReactNode } from "react";
import { DICTS, type Locale } from "./dict";
import { LocaleContext, bcp47, localeFor, localeFromQuery, storeLocale }
  from "./locale";

export function LocaleProvider({ handle, children }: {
  /** Wie er kijkt, zodra dat bekend is. Bepaalt alléén de standaardtaal. */
  handle?: "R" | "N" | null | undefined;
  children: ReactNode;
}) {
  /* Alleen de expliciete keuze van deze sessie is state. `null` betekent
     «nog niets aangeraakt», en dan geldt wat `localeFor` uitrekent. */
  const [chosen, setChosen] = useState<Locale | null>(() => localeFromQuery());

  const locale = chosen ?? localeFor(handle);

  const code = bcp47(locale);
  if (document.documentElement.lang !== code)
    document.documentElement.lang = code;

  const setLocale = useCallback((l: Locale) => {
    storeLocale(l);                      // een tik is een keuze, en die blijft
    setChosen(l);
  }, []);

  const value = useMemo(
    () => ({ locale, t: DICTS[locale], setLocale }), [locale, setLocale]);

  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
}
